import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'vesper-offline';
const DB_VERSION = 1;
const SCRIPTS_STORE = 'scripts';
const TOKEN_STORE = 'offline-token';
const SYNC_QUEUE_STORE = 'sync-queue';

export interface CachedScript {
  id: string;
  title: string;
  content: string;
  settings: object;
  scene_type: string;
  word_count: number;
  cachedAt: Date;
}

export interface OfflineToken {
  subscriptionTier: 'pro' | 'basic' | 'free';
  validatedAt: Date;
  expiresAt: Date;
}

export interface SyncQueueItem {
  id: string;
  scriptId: string;
  action: 'update' | 'delete';
  data?: Partial<CachedScript>;
  timestamp: Date;
}

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      if (!db.objectStoreNames.contains(SCRIPTS_STORE)) {
        db.createObjectStore(SCRIPTS_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(TOKEN_STORE)) {
        db.createObjectStore(TOKEN_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
        db.createObjectStore(SYNC_QUEUE_STORE, { keyPath: 'id' });
      }
    };
  });
};

export const useOfflineStorage = () => {
  const [cachedScripts, setCachedScripts] = useState<CachedScript[]>([]);
  const [offlineToken, setOfflineToken] = useState<OfflineToken | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadCachedScripts = useCallback(async () => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SCRIPTS_STORE, 'readonly');
      const store = transaction.objectStore(SCRIPTS_STORE);
      const request = store.getAll();

      return new Promise<CachedScript[]>((resolve) => {
        request.onsuccess = () => {
          const scripts = request.result || [];
          setCachedScripts(scripts);
          resolve(scripts);
        };
        request.onerror = () => resolve([]);
      });
    } catch {
      return [];
    }
  }, []);

  const cacheScript = useCallback(async (script: Omit<CachedScript, 'cachedAt'>): Promise<boolean> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SCRIPTS_STORE, 'readwrite');
      const store = transaction.objectStore(SCRIPTS_STORE);

      // Check if we already have 10 scripts cached
      const countRequest = store.count();
      const count = await new Promise<number>((resolve) => {
        countRequest.onsuccess = () => resolve(countRequest.result);
        countRequest.onerror = () => resolve(0);
      });

      // Check if this script is already cached
      const existingRequest = store.get(script.id);
      const existing = await new Promise<CachedScript | undefined>((resolve) => {
        existingRequest.onsuccess = () => resolve(existingRequest.result);
        existingRequest.onerror = () => resolve(undefined);
      });

      if (count >= 10 && !existing) {
        return false; // Max scripts reached
      }

      const cachedScript: CachedScript = {
        ...script,
        cachedAt: new Date()
      };

      const putRequest = store.put(cachedScript);
      return new Promise((resolve) => {
        putRequest.onsuccess = () => {
          loadCachedScripts();
          resolve(true);
        };
        putRequest.onerror = () => resolve(false);
      });
    } catch {
      return false;
    }
  }, [loadCachedScripts]);

  const removeCachedScript = useCallback(async (scriptId: string): Promise<boolean> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SCRIPTS_STORE, 'readwrite');
      const store = transaction.objectStore(SCRIPTS_STORE);
      const request = store.delete(scriptId);

      return new Promise((resolve) => {
        request.onsuccess = () => {
          loadCachedScripts();
          resolve(true);
        };
        request.onerror = () => resolve(false);
      });
    } catch {
      return false;
    }
  }, [loadCachedScripts]);

  const getCachedScript = useCallback(async (scriptId: string): Promise<CachedScript | null> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SCRIPTS_STORE, 'readonly');
      const store = transaction.objectStore(SCRIPTS_STORE);
      const request = store.get(scriptId);

      return new Promise((resolve) => {
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }, []);

  const isScriptCached = useCallback((scriptId: string): boolean => {
    return cachedScripts.some(s => s.id === scriptId);
  }, [cachedScripts]);

  // Offline Token Management
  const saveOfflineToken = useCallback(async (tier: 'pro' | 'basic' | 'free'): Promise<boolean> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(TOKEN_STORE, 'readwrite');
      const store = transaction.objectStore(TOKEN_STORE);

      const now = new Date();
      const token: OfflineToken & { id: string } = {
        id: 'current',
        subscriptionTier: tier,
        validatedAt: now,
        expiresAt: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) // 7 days
      };

      const request = store.put(token);
      return new Promise((resolve) => {
        request.onsuccess = () => {
          setOfflineToken(token);
          resolve(true);
        };
        request.onerror = () => resolve(false);
      });
    } catch {
      return false;
    }
  }, []);

  const loadOfflineToken = useCallback(async (): Promise<OfflineToken | null> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(TOKEN_STORE, 'readonly');
      const store = transaction.objectStore(TOKEN_STORE);
      const request = store.get('current');

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const token = request.result;
          if (token) {
            // Convert date strings back to Date objects
            token.validatedAt = new Date(token.validatedAt);
            token.expiresAt = new Date(token.expiresAt);
            setOfflineToken(token);
          }
          resolve(token || null);
        };
        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }, []);

  const isOfflineTokenValid = useCallback((): boolean => {
    if (!offlineToken) return false;
    if (offlineToken.subscriptionTier !== 'pro') return false;
    return new Date() < new Date(offlineToken.expiresAt);
  }, [offlineToken]);

  // Sync Queue Management
  const addToSyncQueue = useCallback(async (item: Omit<SyncQueueItem, 'id' | 'timestamp'>): Promise<boolean> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SYNC_QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);

      const queueItem: SyncQueueItem = {
        ...item,
        id: `${item.scriptId}-${Date.now()}`,
        timestamp: new Date()
      };

      const request = store.put(queueItem);
      return new Promise((resolve) => {
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
      });
    } catch {
      return false;
    }
  }, []);

  const getSyncQueue = useCallback(async (): Promise<SyncQueueItem[]> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SYNC_QUEUE_STORE, 'readonly');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.getAll();

      return new Promise((resolve) => {
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => resolve([]);
      });
    } catch {
      return [];
    }
  }, []);

  const clearSyncQueue = useCallback(async (): Promise<boolean> => {
    try {
      const db = await openDB();
      const transaction = db.transaction(SYNC_QUEUE_STORE, 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const request = store.clear();

      return new Promise((resolve) => {
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
      });
    } catch {
      return false;
    }
  }, []);

  // Initialize
  useEffect(() => {
    const init = async () => {
      await loadCachedScripts();
      await loadOfflineToken();
      setIsLoading(false);
    };
    init();
  }, [loadCachedScripts, loadOfflineToken]);

  return {
    cachedScripts,
    isLoading,
    cacheScript,
    removeCachedScript,
    getCachedScript,
    isScriptCached,
    offlineToken,
    saveOfflineToken,
    isOfflineTokenValid,
    addToSyncQueue,
    getSyncQueue,
    clearSyncQueue
  };
};
